import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { useApp } from '@/context/AppContext';
import BackofficeLayout from '@/components/layout/BackofficeLayout';
import MerchantPendingScreen from '@/components/MerchantPendingScreen';

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="flex items-center justify-center h-screen text-muted-foreground">{t('misc_common.loading')}</div>;
  if (!user) return <Navigate to="/login" replace />;
  // FR-96: an invited account works only after its temporary password is replaced.
  if (user.must_change_password && location.pathname !== '/change-password') return <Navigate to="/change-password" replace />;
  return <>{children}</>;
}

/** Routes that require an active merchant (not 'new', 'rejected', 'blocked', or 'frozen') */
export function ActiveMerchantRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const status = user?.merchant_platform_status;
  if (status === 'new' || status === 'rejected' || status === 'blocked' || status === 'frozen') {
    return <MerchantPendingScreen />;
  }
  return <>{children}</>;
}

export function BackofficeRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <ActiveMerchantRoute>
        <BackofficeLayout>{children}</BackofficeLayout>
      </ActiveMerchantRoute>
    </ProtectedRoute>
  );
}

/** Profile route - accessible even when merchant is 'new' */
export function ProfileRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <BackofficeLayout>{children}</BackofficeLayout>
    </ProtectedRoute>
  );
}

/** Flows that sell the cart (murabaha) make no sense without goods: a direct URL goes back to the POS */
export function NonEmptyCartRoute({ children }: { children: React.ReactNode }) {
  const { cart } = useApp();
  if (cart.length === 0) return <Navigate to="/pos" replace />;
  return <>{children}</>;
}
